// src/cockpit4/components/WebsiteControlPanel.tsx
// ============================================
// 官网管控中台（v6.3.9）- 经营中心 Tab
//
// 功能：
//   1. 官网访问统计：今日访问量 / 独立访客 / 近7天趋势 / 页面TOP10 / 最近访问
//   2. 工具页评论管理：查看全部评论（含IP）、管理员删除恶意评论
//   3. 一键打开官网统计页（带口令）
//
// 数据源：os.xshetech.com（与 XEAOS 系统同源，直接 fetch）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Globe,
  RefreshCw,
  Loader2,
  Eye,
  Users,
  BarChart3,
  MessageSquare,
  Trash2,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

interface StatsData {
  totalVisits: number;
  todayVisits: number;
  todayUniqueIps: number;
  days: { date: string; visits: number; uniqueIps: number }[];
  topPages: { page: string; count: number }[];
  recent: { ts: number; ip: string; page: string; title: string; referrer: string }[];
}

interface FeedbackItem {
  id: string;
  nickname: string;
  score: number;
  content: string;
  ts: number;
  ip?: string;
  edited?: boolean;
}

const ADMIN_KEY = "xeaos2026"; // 与官网 stats/fb 管理口令一致
const STATS_API = "https://os.xshetech.com/stats?key=" + ADMIN_KEY + "&days=7";
const FB_LIST_API = "https://os.xshetech.com/feedback-list?key=" + ADMIN_KEY;
const FB_DEL_API = "https://os.xshetech.com/feedback-delete";

// ============================================
// 主组件
// ============================================

export default function WebsiteControlPanel() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingFb, setLoadingFb] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const resp = await fetch(STATS_API);
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || "加载失败");
      setStats(data);
      setLastUpdated(new Date().toLocaleTimeString("zh-CN", { hour12: false }));
    } catch (err) {
      setMessage({ type: "err", text: `统计加载失败：${(err as Error).message}` });
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadFeedbacks = useCallback(async () => {
    setLoadingFb(true);
    try {
      const resp = await fetch(FB_LIST_API);
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || "加载失败");
      setFeedbacks(data.items || []);
    } catch (err) {
      setMessage({ type: "err", text: `评论加载失败：${(err as Error).message}` });
    } finally {
      setLoadingFb(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
    void loadFeedbacks();
  }, [loadStats, loadFeedbacks]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("确定删除这条评论吗？（删除后前台不再展示）")) return;
    setDeleting(id);
    try {
      const resp = await fetch(FB_DEL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, key: ADMIN_KEY }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || "删除失败");
      setFeedbacks((prev) => prev.filter((f) => f.id !== id));
      setMessage({ type: "ok", text: "✅ 评论已删除" });
    } catch (err) {
      setMessage({ type: "err", text: `删除失败：${(err as Error).message}` });
    } finally {
      setDeleting(null);
    }
  };

  const summary = useMemo(() => {
    if (!stats) return null;
    const maxVisits = Math.max(1, ...stats.days.map((d) => d.visits));
    const avgScore =
      feedbacks.length > 0
        ? (feedbacks.reduce((s, f) => s + f.score, 0) / feedbacks.length).toFixed(1)
        : "—";
    return { maxVisits, avgScore };
  }, [stats, feedbacks]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden h-full"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Globe size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">官网管控中台</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              xshetech.com · 访问统计 + 评论管理 · 更新于 {lastUpdated || "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://www.xshetech.com/stats.html?key=xeaos2026"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 transition-colors text-[9px] font-mono uppercase"
          >
            <ExternalLink size={10} /> 官网统计页
          </a>
          <button
            onClick={() => { void loadStats(); void loadFeedbacks(); }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase">刷新</span>
          </button>
        </div>
      </div>

      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className={`px-3 py-2 rounded-md border text-[10px] font-mono ${message.type === "ok" ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300" : "border-red-500/30 bg-red-500/[0.06] text-red-300"}`}>
            {message.text}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-220px)]">
        {/* ============ 访问统计 ============ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Eye size={12} className="text-cyan-400" />
            <span className="text-[11px] font-medium text-gray-300">访问统计</span>
          </div>

          {loadingStats && !stats ? (
            <div className="flex items-center gap-2 py-8 justify-center text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-[11px] font-mono">加载统计中…</span>
            </div>
          ) : stats ? (
            <>
              {/* KPI 卡 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                  <div className="text-[18px] font-bold text-cyan-300">{stats.todayVisits}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">今日访问量</div>
                </div>
                <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                  <div className="text-[18px] font-bold text-emerald-300">{stats.todayUniqueIps}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">今日独立访客</div>
                </div>
                <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                  <div className="text-[18px] font-bold text-violet-300">{stats.totalVisits}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">累计访问量</div>
                </div>
                <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                  <div className="text-[18px] font-bold text-amber-300">{feedbacks.length}</div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">累计评论</div>
                </div>
              </div>

              {/* 近7天趋势 */}
              <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3 mb-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <BarChart3 size={11} className="text-gray-500" />
                  <span className="text-[10px] font-mono text-gray-400">近 7 天访问趋势</span>
                </div>
                <div className="space-y-1">
                  {stats.days.map((d) => (
                    <div key={d.date} className="flex items-center gap-2">
                      <span className="w-[70px] text-[9px] font-mono text-gray-500 flex-shrink-0">{d.date.slice(5)}</span>
                      <div className="flex-1 h-[14px] bg-white/[0.04] rounded overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500/70 to-violet-500/70 rounded"
                          style={{ width: `${Math.max(2, (d.visits / (summary?.maxVisits || 1)) * 100)}%` }}
                        />
                      </div>
                      <span className="w-[76px] text-[9px] font-mono text-gray-400 text-right flex-shrink-0">{d.visits}次/{d.uniqueIps}IP</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 页面 TOP */}
              <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                <div className="text-[10px] font-mono text-gray-400 mb-2">页面访问 TOP 5（用户最关心什么）</div>
                {(stats.topPages || []).slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-white/[0.03] last:border-0">
                    <span className="text-[10px] text-gray-300 truncate pr-2">
                      {i + 1}. {p.page || "/"}
                    </span>
                    <span className="text-[10px] font-mono text-cyan-300 flex-shrink-0">{p.count} 次</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/* ============ 评论管理 ============ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={12} className="text-violet-400" />
            <span className="text-[11px] font-medium text-gray-300">工具页评论管理</span>
            <span className="text-[9px] font-mono text-gray-600">AI口碑体检 + AI味检测器 · 管理员可删除</span>
          </div>

          {loadingFb ? (
            <div className="flex items-center gap-2 py-6 justify-center text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-[11px] font-mono">加载评论…</span>
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-[11px] font-mono text-gray-600">暂无评论</p>
              <p className="text-[9px] font-mono text-gray-700 mt-1">官网工具页有用户反馈后会显示在这里</p>
            </div>
          ) : (
            <div className="space-y-2">
              {feedbacks.map((f) => {
                const stars = "★★★★★".slice(0, f.score) + "☆☆☆☆☆".slice(0, 5 - f.score);
                const t = new Date(f.ts).toLocaleString("zh-CN", { hour12: false });
                return (
                  <div key={f.id} className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-200">{f.nickname}</span>
                      <span className="text-[10px] text-amber-400">{stars}</span>
                      <span className="text-[9px] font-mono text-gray-600">{t}</span>
                      {f.ip && (
                        <span className="text-[9px] font-mono text-gray-600 border border-white/[0.06] rounded px-1 py-px">IP:{f.ip}</span>
                      )}
                      {f.edited && <span className="text-[9px] text-gray-600">已编辑</span>}
                      <button
                        onClick={() => handleDelete(f.id)}
                        disabled={deleting === f.id}
                        className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      >
                        {deleting === f.id ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        <span className="text-[9px] font-mono">删除</span>
                      </button>
                    </div>
                    <div className="text-[11px] text-gray-400 leading-relaxed">{f.content}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
          <div className="flex items-start gap-2">
            <ShieldCheck size={13} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-[10px] text-emerald-300/80 leading-relaxed">
              官网管控中台 = 官网一切的"后台驾驶舱"：访问数据、用户行为、评论反馈统一在这里查看与处置。
              恶意评论删除后官网前台立即不可见（数据软删可追溯）。评论人与管理员都可删除自己的评论。
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
